import type { EtlJob } from '../../types/etlJobs'

/** One heading's worth of the Scheduler. */
export interface JobGroup {
  /** Stable React key; the heading carries a count and so is not one. */
  key: string
  heading: string
  jobs: EtlJob[]
  /** Entity jobs are sub-grouped by module; everything else is a flat list. */
  modules?: { module: string; jobs: EtlJob[] }[]
  /**
   * True for the catch-all group. Its rows show their category, because the heading no longer says it.
   */
  showsCategory?: boolean
}

/** The categories this page knows by name, in the order the API ranks them. */
const KNOWN_CATEGORIES = ['Warehouse', 'Entity', 'Maintenance', 'Diagnostics'] as const

/**
 * Groups the catalogue the way an operator reads it: the whole-warehouse load, then entity jobs by module,
 * then maintenance, then diagnostics, then anything else.
 *
 * Within a module, entity jobs keep dependency-level order rather than alphabetical — the level is the
 * meaningful sequence, and it is the order the orchestrator uses. The flat groups keep the order the
 * catalogue served, which the API already sorts by level and name.
 *
 * NOTHING IS DROPPED. This used to keep only Warehouse, Entity and Maintenance, so the Data Comb
 * (Diagnostics) never appeared while the Data Comb page told people to start it here. The last group
 * mirrors the API's `CategoryRank` fall-through (`_ => 4`, EtlJobCatalogController): a category added in C#
 * tomorrow lands under "Other" instead of vanishing.
 */
export function groupJobs(jobs: EtlJob[]): JobGroup[] {
  const warehouse = jobs.filter((j) => j.category === 'Warehouse')
  const entities = jobs.filter((j) => j.category === 'Entity')
  const maintenance = jobs.filter((j) => j.category === 'Maintenance')
  const diagnostics = jobs.filter((j) => j.category === 'Diagnostics')
  const other = jobs.filter((j) => !(KNOWN_CATEGORIES as readonly string[]).includes(j.category))

  const groups: JobGroup[] = []

  if (warehouse.length > 0) {
    groups.push({ key: 'warehouse', heading: 'Warehouse', jobs: warehouse })
  }

  if (entities.length > 0) {
    const byModule = new Map<string, EtlJob[]>()
    for (const job of entities) {
      const module = job.module ?? 'Other'
      byModule.set(module, [...(byModule.get(module) ?? []), job])
    }

    groups.push({
      key: 'entities',
      heading: `Entities (${entities.length})`,
      jobs: entities,
      modules: [...byModule.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([module, moduleJobs]) => ({
          module,
          jobs: [...moduleJobs].sort(
            (a, b) =>
              (a.levelNumber ?? 0) - (b.levelNumber ?? 0) ||
              a.displayName.localeCompare(b.displayName)
          ),
        })),
    })
  }

  if (maintenance.length > 0) {
    groups.push({ key: 'maintenance', heading: 'Maintenance', jobs: maintenance })
  }

  if (diagnostics.length > 0) {
    groups.push({ key: 'diagnostics', heading: 'Diagnostics', jobs: diagnostics })
  }

  if (other.length > 0) {
    groups.push({ key: 'other', heading: 'Other', jobs: other, showsCategory: true })
  }

  return groups
}
